import "./IcebreakerResponses.scss";

import { Avatar, Flex, Text } from "@fluentui/react-northstar";
import { EmojiReactionType, TeamsSurveyEmojiResponse, TeamsSurveyResponse } from "@teams/types";
import { SurveyDTO } from "@teams/types/api";
import moment from "moment";
import { useCallback } from "react";

import { EmojiReactions } from "../../../../../_shared/components/EmojiReactions";
import { surveyApi } from "../../../../../_shared/services/SurveyApi";
import { useTeamsContext } from "../../../../../_shared/state/TeamsContextProvider";

type IcebreakerResponsesProps = {
  survey: SurveyDTO;
  responses: TeamsSurveyResponse[];
  totalResponses: number;
  onReactionChange: (reaction: EmojiReactionType) => void;
};

export const IcebreakerResponses: React.FC<IcebreakerResponsesProps> = ({
  survey,
  responses,
  totalResponses,
  onReactionChange,
}) => {
  responses = responses.sort((a, b) => {
    const aTime = a.completedOn?.getTime() || 0;
    const bTime = b.completedOn?.getTime() || 0;
    return bTime - aTime;
  });

  return (
    <>
      {totalResponses === 0 && <Text content="Be the first to respond!" weight="semibold" />}
      {totalResponses > 0 && (
        <IcebreakerResponsesList
          survey={survey}
          responses={responses}
          onReactionChange={onReactionChange}
        />
      )}
    </>
  );
};

const IcebreakerResponsesList: React.FC<{
  survey: SurveyDTO;
  responses: TeamsSurveyResponse[];
  onReactionChange: (reaction: EmojiReactionType) => void;
}> = ({ survey, responses, onReactionChange }) => {
  return (
    <>
      <Text content={`Total submissions: ${responses.length}`} weight="semibold" />
      {responses.map(response => (
        <IcebreakerResponseItem
          key={response.id}
          survey={survey}
          response={response}
          onReactionChange={onReactionChange}
        />
      ))}
    </>
  );
};

const IcebreakerResponseItem: React.FC<{
  survey: SurveyDTO;
  response: TeamsSurveyResponse;
  onReactionChange: (reaction: EmojiReactionType) => void;
}> = ({ survey, response, onReactionChange }) => {
  const reactions = (response as TeamsSurveyEmojiResponse).reactions || [];
  const { teamsContext } = useTeamsContext();

  const handleReactionChange = useCallback(
    async (reaction: EmojiReactionType) => {
      await surveyApi.toggleEmojiReaction(
        teamsContext,
        survey.id!,
        response.id,
        "icebreaker",
        reaction
      );

      onReactionChange(reaction);
    },
    [teamsContext, survey.id, response.id, onReactionChange]
  );

  return (
    <Flex column gap="gap.smaller" className="icebreaker-responses__response-item">
      <div className="icebreaker-responses__response-item__header">
        <Flex gap="gap.smaller" vAlign="center">
          <Avatar name={response.user.name || "Anonymous"} size="smallest" />
          <Text content={response.user.name} size="small" />
        </Flex>

        <Text
          content={`${moment.duration(moment().diff(response.createdAt)).humanize()} ago`}
          weight="light"
          size="smaller"
        />
      </div>

      <Text content={response.responses[0].value} size="smallest" />
      <EmojiReactions
        survey={survey}
        reactions={reactions}
        onReactionChange={handleReactionChange}
      />
    </Flex>
  );
};
